import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useHttpClient } from "../../hooks/useHttpClient";
import { useLiveSessionStore } from "../../stores/useLiveSessionStore";
import { useUserContext } from "../../stores/userContext";
import useDimensions from "../../utils/SessionDimensions";
import { Loading } from "../Common/Loading/Loading";
import "./LiveSession.scss";
import { ScreenSlides } from "./ScreenSlides/ScreenSlides";
import { SessionControllers } from "./SessionControllers/SessionControllers";
import { Sidebar } from "./Sidebar/Sidebar";
import { Whiteboard } from "./Whiteboard/Whiteboard";

export const LiveSession = () => {
  const {
    language,
    sessionId,
    materialId: urlMaterialId,
  } = useParams<{
    language: string;
    sessionId: string;
    materialId: string;
  }>();

  const navigate = useNavigate();
  const { user } = useUserContext();
  const api = useHttpClient();

  // ✅ Get MD5 hash from sessionStorage if available
  const materialMd5 = useMemo(() => {
    const storedMd5 = sessionStorage.getItem(
      `session_${sessionId}_material_md5`
    );
    console.log(
      `[LiveSession] Retrieved MD5 for session ${sessionId}:`,
      storedMd5
    );
    return storedMd5 || undefined;
  }, [sessionId]);

  // Zustand live session store
  const startSession = useLiveSessionStore((state) => state.startSession);
  const sessionData = useLiveSessionStore((state) => state.sessionData);
  const sessionError = useLiveSessionStore((state) => state.sessionError);
  const isEngineLoaded = useLiveSessionStore((state) => state.isEngineLoaded);
  // const activeSlideIndex = useLiveSessionStore(
  //   (state) => state.activeSlideIndex
  // );
  const screenDataCount = useLiveSessionStore(
    (state) => state.screenData.length
  );

  const {
    margins,
    sideBarWidth,
    contentHeight,
    drawingAreaWidth,
    screensHeight,
  } = useDimensions();

  const [isSendingEngagement, setIsSendingEngagement] = useState(false); // Manage state

  // Start live session
  useEffect(() => {
    // ✅ Validate required URL parameters
    if (!language || !sessionId || !urlMaterialId) {
      console.error("Missing required URL parameters:", {
        language,
        sessionId,
        materialId: urlMaterialId,
      });
      navigate("/");
      return;
    }

    // ✅ Check user authentication
    if (!user?.userId) {
      console.error("No user logged in");
      return;
    }

    // ✅ Start live session via Zustand store
    startSession({
      language,
      sessionId,
      materialId: urlMaterialId,
      materialMd5,
      userId: user.userId,
      apiPost: api.post,
    });
  }, [
    language, // Added missing dependency
    sessionId,
    urlMaterialId,
    user?.userId,
    startSession,
    api.post,
    navigate,
    materialMd5,
  ]);

  // log or show errors
  useEffect(() => {
    if (sessionError) {
      console.error("Live session error:", sessionError);
    }
  }, [sessionError]);

  return (
    <>
      {sessionData && (
        <div
          className="live-session-container"
          style={{
            // overflow: "hidden",
            marginTop: margins.tm,
            marginLeft: margins.lm,
            marginRight: margins.rm,
            marginBottom: margins.bm,
          }}
        >
          <SessionControllers setIsSendingEngagement={setIsSendingEngagement} />
          <ScreenSlides height={screensHeight} />
          <div className="live-session-body" style={{ height: contentHeight }}>
            <Whiteboard width={drawingAreaWidth} />
            {isEngineLoaded && screenDataCount > 0 && (
              <Sidebar
                width={sideBarWidth}
                isSendingEngagement={isSendingEngagement}
              />
            )}
          </div>
        </div>
      )}

      {/* Loading overlay on top of the live session until engine and slides are ready */}
      {sessionData && (!isEngineLoaded || screenDataCount === 0) && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 9998,
            background: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Loading />
        </div>
      )}
    </>
  );
};
